import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Html5QrcodeScanner } from "html5-qrcode";
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db, auth } from "../services/firebase";
import { createAssetReturnNotification } from "../services/notifications";
import { addLog } from "../utils/audit";
import { QrCode, CheckCircle, AlertTriangle, RefreshCw, ArrowLeft } from "lucide-react";

export default function StudentReturnScan() {
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState("");
  const [scannedAsset, setScannedAsset] = useState(null);
  const [toast, setToast] = useState("");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const scannerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(setUser);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!scanning || !user) return;

    // Set mounted flag
    isMountedRef.current = true;

    const scanner = new Html5QrcodeScanner("reader", {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
    });

    // Store scanner reference
    scannerRef.current = scanner;

    const onScanSuccess = async (decodedText) => {
      // Don't process if component is unmounting
      if (!isMountedRef.current) return;

      try {
        let assetId;
        if (decodedText.includes('/asset/')) {
          const url = new URL(decodedText);
          assetId = url.pathname.split('/asset/')[1];
        } else if (decodedText.includes('id=')) {
          const url = new URL(decodedText);
          assetId = url.searchParams.get('id');
        } else {
          assetId = decodedText;
        }

        if (!assetId) {
          setError("Invalid QR code: No asset ID found");
          return;
        }

        const assetDoc = await getDoc(doc(db, "assets", assetId));
        if (!assetDoc.exists()) {
          setError("Asset not found in database");
          return;
        }

        const assetData = { id: assetId, ...assetDoc.data() };

        // Check if this asset is borrowed by the current user
        const requestsQuery = query(
          collection(db, "requests"),
          where("requesterId", "==", user.uid),
          where("assetId", "==", assetId),
          where("type", "==", "borrow"),
          where("status", "==", "approved")
        );
        const requestsSnap = await getDocs(requestsQuery);

        if (requestsSnap.empty) {
          setError("This asset is not currently borrowed by you");
          return;
        }

        setScannedAsset(assetData);
        setScanning(false);

        // Only clear scanner if component is still mounted
        if (isMountedRef.current && scannerRef.current) {
          scannerRef.current.clear().catch(err => {
            // Ignore cleanup errors when component is unmounting
            if (isMountedRef.current) {
              console.error("Failed to clear scanner:", err);
            }
          });
        }
      } catch (err) {
        console.error("Error processing QR code:", err);
        setError("Invalid QR code format");
      }
    };

    const onScanFailure = (error) => {
      if (!error.includes("No MultiFormat Readers")) {
        console.warn("QR Scan error:", error);
      }
    };

    scanner.render(onScanSuccess, onScanFailure);

    return () => {
      // Mark as unmounting
      isMountedRef.current = false;

      // Clear scanner only if it exists and we're still mounted
      if (scannerRef.current) {
        scannerRef.current.clear().catch(err => {
          // Ignore cleanup errors during unmount
          console.debug("Scanner cleanup during unmount:", err.message);
        });
        scannerRef.current = null;
      }
    };
  }, [scanning, user]);

  const handleReturnAsset = async () => {
    if (!scannedAsset || !user) return;

    try {
      // Update asset status to available
      await updateDoc(doc(db, "assets", scannedAsset.id), {
        status: "available",
        lastUpdated: new Date(),
        returnedBy: user.uid,
        returnedAt: new Date(),
      });

      // Find and update the corresponding request
      const requestsQuery = query(
        collection(db, "requests"),
        where("requesterId", "==", user.uid),
        where("assetId", "==", scannedAsset.id),
        where("type", "==", "borrow"),
        where("status", "==", "approved")
      );
      const requestsSnap = await getDocs(requestsQuery);

      if (!requestsSnap.empty) {
        const requestDoc = requestsSnap.docs[0];
        const requestData = requestDoc.data();

        // Update request status
        await updateDoc(doc(db, "requests", requestDoc.id), {
          status: "returned",
          returnedAt: new Date(),
          lastUpdated: new Date(),
        });

        // Create notification for admin
        const returnDataWithId = { ...requestData, id: requestDoc.id };
        await createAssetReturnNotification(returnDataWithId);

        // Add to audit trail
        await addLog(user.uid, "asset_return", {
          assetId: scannedAsset.id,
          assetName: scannedAsset.name,
          location: scannedAsset.location,
          returnedAt: new Date(),
        });
      }

      setToast(`✅ ${scannedAsset.name || scannedAsset.id} returned successfully!`);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error("Failed to return asset:", err);
      setError("Failed to return asset. Please try again.");
    }
  };

  const restartScanning = () => {
    setError("");
    setScannedAsset(null);
    setScanning(true);
  };

  return (
    <div className="min-h-screen bg-neutral-50 animate-fade-in">
      <div className="container-fluid pt-6 pb-6">
        <div className="max-w-md mx-auto space-y-6">
          {/* Header Section */}
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <QrCode className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">
              Scan Asset QR to Return
            </h1>
            <p className="text-neutral-600">
              Point your camera at the asset's QR code to return it
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-xl p-4 animate-slide-in">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-error-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-error-900">Error</p>
                  <p className="text-sm text-error-700 mt-1">{error}</p>
                  <button
                    onClick={restartScanning}
                    className="btn-error mt-3 px-4 py-2 text-sm"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Toast */}
          {toast && (
            <div className="bg-success-50 border border-success-200 rounded-xl p-4 animate-bounce-in">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-success-600" />
                <p className="text-success-900 font-medium">{toast}</p>
              </div>
            </div>
          )}

          {scannedAsset ? (
            <div className="space-y-6 animate-fade-in">
              {/* Asset Info Card */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-neutral-900">
                    {scannedAsset.name || "Unnamed Asset"}
                  </h2>
                  <span className="status-info">
                    Ready to Return
                  </span>
                </div>

                <div className="space-y-3 text-sm text-neutral-600">
                  <div className="flex items-center gap-3">
                    <QrCode className="w-4 h-4 text-neutral-500" />
                    <span>ID: {scannedAsset.id}</span>
                  </div>
                  {scannedAsset.location && (
                    <div className="flex items-center gap-3">
                      <span>Location: {scannedAsset.location}</span>
                    </div>
                  )}
                  {scannedAsset.category && (
                    <div className="flex items-center gap-3">
                      <span>Category: {scannedAsset.category}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Return Confirmation */}
              <div className="card">
                <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-success-600" />
                  Confirm Return
                </h3>
                <p className="text-neutral-700 mb-4">
                  Are you sure you want to return <span className="font-medium">{scannedAsset.name || scannedAsset.id}</span>?
                </p>
                <p className="text-sm text-neutral-600 mb-4">
                  This will mark the asset as available and complete your borrow request.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleReturnAsset}
                    className="btn-success flex-1 py-3 text-sm flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Return Asset
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Scanner Interface */
            <div id="reader" className="w-full overflow-hidden rounded-xl bg-gray-100"></div>
          )}

        </div>
      </div>
    </div>
  );
}
